import { calendarDateOf, parseWeekKey, toWeekKey, type WeekKey } from "./dates";

/**
 * The selected week's two mirrors outside React: `?week=` in the address bar
 * and a key on `history.state` (R1-C step 4).
 *
 * App.tsx owns ONE `selectedWeek`. `null` means "follow the current week" and
 * is never written anywhere, so an ordinary session keeps a clean URL; a
 * `WeekKey` is an explicit choice that must survive a reload, a shared link,
 * and Back/Forward.
 *
 * The URL is the source of truth. `history.state` is a fallback for an entry
 * whose URL lost the param (another writer calling replaceState with a bare
 * URL), and it is what a popstate hands back.
 *
 * Same Next.js caveat as view-history.ts: the App Router patches native
 * pushState/replaceState and stores its own keys on the state object, so this
 * key is always MERGED into the existing state and never replaces it.
 * Everything here goes through `parseWeekKey`, so no unchecked string from
 * the URL can become a `WeekKey`.
 */
export const SELECTED_WEEK_PARAM = "week";
export const SELECTED_WEEK_HISTORY_KEY = "bbWeek";

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/**
 * The week an entry carries, or null. Never throws. A mid-week date is
 * normalised to its Sunday; a malformed `?week=` falls through to the state.
 */
export function readSelectedWeek(search: string, state: unknown): WeekKey | null {
  const fromUrl = parseWeekKey(new URLSearchParams(search).get(SELECTED_WEEK_PARAM));
  if (fromUrl) return fromUrl;
  return isRecord(state) ? parseWeekKey(state[SELECTED_WEEK_HISTORY_KEY]) : null;
}

const MILLIS_PER_WEEK = 7 * 86_400_000;

/**
 * `week` moved by whole weeks. UTC arithmetic on the calendar date, never local
 * time: a local week with a DST change is 167 or 169 hours long.
 */
export function shiftWeekKey(week: WeekKey, weeks: number): WeekKey {
  const shifted = new Date(new Date(`${week}T00:00:00.000Z`).getTime() + weeks * MILLIS_PER_WEEK);
  return shifted.toISOString().slice(0, 10) as WeekKey;
}

/**
 * What to store in order to show `week`. The current week is stored as null
 * ("follow the current week"), never as its own WeekKey: an ordinary session
 * keeps a clean URL, and a tab left open over Sunday moves on with the calendar
 * instead of staying pinned to last week.
 */
export function selectionForWeek(week: WeekKey, currentWeek: WeekKey): WeekKey | null {
  return week === currentWeek ? null : week;
}

/**
 * What to store for Previous/Next. Lifted here so the Planner and Groceries
 * share one rule rather than two copies of it: step from the selection if there
 * is one, else from the current week, and store the result through
 * `selectionForWeek` so stepping back onto the current week leaves a clean URL.
 *
 * `now` is passed in, never read from the clock here: the caller reads it at
 * click time, which is what makes a tab left open over Sunday step from the
 * week it is actually showing.
 */
export function selectionForStep(
  selected: WeekKey | null,
  weeks: -1 | 1,
  now: Date,
): WeekKey | null {
  const currentWeek = toWeekKey(now);
  return selectionForWeek(shiftWeekKey(selected ?? currentWeek, weeks), currentWeek);
}

/**
 * What to store in order to show the week a STORED plan date falls in, or
 * `undefined` when the value is not a calendar date.
 *
 * `undefined` is deliberately distinct from `null`: `null` means "select the
 * current week" and would MOVE the viewer, which is the wrong answer for a
 * value we failed to read. A caller that cannot tell the two apart would
 * silently send someone to this week on any malformed date.
 *
 * `calendarDateOf` first, never `new Date(...)` with local accessors: a
 * `@db.Date` serialises as "YYYY-MM-DDT00:00:00.000Z", and in every zone west
 * of UTC that instant is the PREVIOUS local day -- for a Sunday, the previous
 * WEEK. That is the whole reason this lives in one place.
 */
export function selectionForStoredDate(
  storedDate: string,
  now: Date,
): WeekKey | null | undefined {
  const week = parseWeekKey(calendarDateOf(storedDate));
  return week === null ? undefined : selectionForWeek(week, toWeekKey(now));
}

/** `href` with `?week=` set (or removed for null), as a same-origin relative href. */
export function hrefWithSelectedWeek(href: string, week: WeekKey | null): string {
  const url = new URL(href);
  url.searchParams.delete(SELECTED_WEEK_PARAM);
  if (week) url.searchParams.set(SELECTED_WEEK_PARAM, week);
  return `${url.pathname}${url.search}${url.hash}`;
}

/** Merges the week into an existing history state object, preserving foreign keys. */
export function withSelectedWeek(state: unknown, week: WeekKey | null): Record<string, unknown> {
  const next: Record<string, unknown> = { ...(isRecord(state) ? state : {}) };
  if (week) next[SELECTED_WEEK_HISTORY_KEY] = week;
  else delete next[SELECTED_WEEK_HISTORY_KEY];
  return next;
}
